import React from 'react';
import styled from 'styled-components';

import { BREAKPOINTS, COLORS, focusStyles, invertedFocusStyles } from '../styles';
import { defaultOnMouseDownHandler } from '../common';
import { Testable } from '../types';
import {TooltipProps} from "../Tooltips/TooltipTypes";
import TooltipWrapper from "../Tooltips/TooltipWrapper";

interface HeaderItemProps {
  hideOnLowWidth: boolean;
  inMobileMenu?: boolean;
  useTransparentBackground?: boolean;
  iconColor?: string;
  unsetIconSize?: boolean;
  borderRadius?: number;
  shape?: string;
  flatEdge?: string;
  focusBackgroundColor?: string;
  invertFocus?: boolean;
  shouldNotInteract?: boolean;
}

const getBorderRadius = (props: HeaderItemProps) => (props.borderRadius ? `${props.borderRadius}px` : '4px');

const getBorderRadiusStyle = (props: HeaderItemProps) => {
  const radius = getBorderRadius(props);
  switch (props.flatEdge) {
    case 'left':
      return `0px ${radius} ${radius} 0px`;

    case 'right':
      return `${radius} 0px 0px ${radius}`;

    case 'none':
    default:
      return `${radius}`;
  }
};

export const IconButtonContentStyles = styled.div``;

export const StyledIconButton = styled.button<HeaderItemProps>`
  border-width: 0;
  cursor: pointer;
  height: 48px;
  width: 48px;
  background: transparent;
  position: relative;

  display: ${(props) => (props.hideOnLowWidth ? 'none' : 'block')};
  ${BREAKPOINTS.MEDIUM} {
    display: block;
  }

  border-radius: ${(props) => (props.borderRadius ? `${props.borderRadius}px` : '4px')};
  ${IconButtonContentStyles} {
    height: 36px;
    width: 36px;
    border-radius: ${getBorderRadiusStyle};
    display: flex;
    justify-content: center;
    align-items: center;
    svg {
      height: ${(props) => (props.unsetIconSize ? 'unset' : '24px')};
      width: ${(props) => (props.unsetIconSize ? 'unset' : '24px')};
      padding: 0;
    }
  }
  &:only-child {
    margin: 0;
  }
  &:disabled {
    cursor: not-allowed;
  }

  &:focus {
    ${(props) => (props.invertFocus ? invertedFocusStyles : focusStyles)}
  }
`;

export const StyledPrimaryIconButton = styled(StyledIconButton)`
  ${IconButtonContentStyles} {
    background-color: ${(props) => (props.useTransparentBackground ? 'transparent' : COLORS.primary_500)};

    svg {
      fill: ${(props) => props.iconColor || COLORS.white};
      path {
        fill: ${(props) => props.iconColor || COLORS.white};
      }
    }
  }
  &:hover:not(:disabled),
  &.hover-state {
    ${IconButtonContentStyles} {
      background-color: ${COLORS.primary_700};
    }
    ${IconButtonContentStyles} svg path,
    ${IconButtonContentStyles} svg {
      fill: ${COLORS.white};
    }
  }
  &:active:not(:disabled),
  &.active-state {
    ${IconButtonContentStyles} {
      background: ${COLORS.primary_800};
    }
    ${IconButtonContentStyles} svg path,
    ${IconButtonContentStyles} svg {
      fill: ${COLORS.white};
    }
  }
  &:disabled {
    ${IconButtonContentStyles} {
      background-color: ${COLORS.neutral_200};
    }
    svg {
      path {
        fill: ${COLORS.white};
      }
      fill: ${COLORS.white};
    }
  }
`;

export const StyledSecondaryIconButton = styled(StyledIconButton)`
  ${IconButtonContentStyles} {
    background-color: ${(props) => (props.useTransparentBackground ? 'transparent' : COLORS.white)};

    svg {
      fill: ${(props) => props.iconColor || COLORS.neutral_600};
      path {
        fill: ${(props) => props.iconColor || COLORS.neutral_600};
      }
    }
  }

  pointer-events: ${(props) => (props.shouldNotInteract ? 'none' : 'auto')};

  &:hover:not(:disabled) {
    ${IconButtonContentStyles} {
      background-color: ${COLORS.primary_20};
    }
    ${IconButtonContentStyles} svg path,
    ${IconButtonContentStyles} svg {
      fill: ${COLORS.primary_700};
    }
  }
  &:active:not(:disabled),
  &.active-state {
    ${IconButtonContentStyles} {
      background: ${COLORS.primary_100};
    }
    ${IconButtonContentStyles} svg path,
    ${IconButtonContentStyles} svg {
      fill: ${COLORS.primary_800};
    }
  }

  &:disabled {
    ${IconButtonContentStyles} {
      background-color: ${(props) => (props.useTransparentBackground ? 'transparent' : COLORS.white)};
    }
    svg {
      path {
        fill: ${COLORS.neutral_300};
      }
      fill: ${COLORS.neutral_300};
    }
  }
`;

export interface IconButtonProps extends Testable, Omit<React.ButtonHTMLAttributes<HTMLButtonElement>,  'onClick' | 'onMouseDown'> {
  variant?: 'primary' | 'secondary';
  shape?: 'square' | 'circular';
  action: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  hideOnLowWidth?: boolean;
  flatEdge?: 'right' | 'left' | 'none' | undefined;
  isInMobileMenu?: boolean;
  useTransparentBackground?: boolean;
  shouldNotInteract?: boolean;
  iconColor?: string;
  unsetIconSize?: boolean;
  borderRadius?: number;
  focusBackgroundColor?: string;
  children?: React.ReactNode;
  invertFocus?: boolean;
  focusOnClick?: boolean;

  tooltip?: Omit<TooltipProps, 'tabIndex'>;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      id,
      variant,
      shape,
      action,
      hideOnLowWidth,
      isInMobileMenu,
      children,
      useTransparentBackground,
      shouldNotInteract,
      disabled,
      iconColor,
      unsetIconSize,
      tabIndex,
      flatEdge,
      borderRadius,
      focusBackgroundColor,
      type,
      hidden,
      invertFocus,
      dataTestId,
      focusOnClick,
      tooltip,
      ...rest
    }: IconButtonProps,
    ref,
  ) => {

    const render = () => {
      // Let's render button
      switch (variant) {
        case 'secondary':
          return (
            <StyledSecondaryIconButton
              id={id}
              type={type ?? 'button'}
              data-testid={dataTestId}
              ref={ref}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                action(event);
              }}
              disabled={disabled}

              hideOnLowWidth={hideOnLowWidth || false}
              inMobileMenu={isInMobileMenu}
              useTransparentBackground={useTransparentBackground}
              shouldNotInteract={shouldNotInteract}
              iconColor={iconColor}
              unsetIconSize={unsetIconSize}
              tabIndex={tabIndex}
              borderRadius={borderRadius || shape === 'circular' ? 18 : 0}
              onMouseDown={!focusOnClick ? defaultOnMouseDownHandler : undefined}
              hidden={hidden}
              invertFocus={invertFocus}
              focusBackgroundColor={focusBackgroundColor}
              {...rest}>
              <IconButtonContentStyles>{children}</IconButtonContentStyles>
            </StyledSecondaryIconButton>
          );
        case 'primary':
        default:
          return (
            <StyledPrimaryIconButton
              id={id}
              type={type ?? 'button'}
              data-testid={dataTestId}
              flatEdge={flatEdge}
              ref={ref}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                action(event);
              }}
              disabled={disabled}
              hideOnLowWidth={hideOnLowWidth || false}
              inMobileMenu={isInMobileMenu}
              useTransparentBackground={useTransparentBackground}
              iconColor={iconColor}
              unsetIconSize={unsetIconSize}
              tabIndex={tabIndex}
              borderRadius={borderRadius || shape === 'circular' ? 18 : 0}
              onMouseDown={!focusOnClick ? defaultOnMouseDownHandler : undefined}
              hidden={hidden}
              invertFocus={invertFocus}
              focusBackgroundColor={focusBackgroundColor}
              {...rest}>
              <IconButtonContentStyles>{children}</IconButtonContentStyles>
            </StyledPrimaryIconButton>
          );
      }
    }

    return !tooltip
      ? render()
      : (
        <TooltipWrapper {...tooltip}>
          {render()}
        </TooltipWrapper>
      );
  },
);

export default IconButton;
